import React, { useState, useEffect } from 'react';
import { RefreshCw, AlertTriangle, Search, Loader, TrendingUp, Wrench } from 'lucide-react';
import { RankedKeywordData } from '../../../lib/types';
import { supabase } from '../../../lib/supabase';
import { callEdge } from '../../../lib/apiClient';

interface KeywordAnalysisCardProps {
  siteUrl: string;
  prospectId?: string;
}

enum AnalysisType {
  RANKED_KEYWORDS = 'ranked_keywords'
}

/**
 * KeywordAnalysisCard - Affiche les mots-clés classés pour un domaine dans un format compact
 */
const KeywordAnalysisCard: React.FC<KeywordAnalysisCardProps> = ({ siteUrl, prospectId }) => {
  const [rankedKeywords, setRankedKeywords] = useState<RankedKeywordData[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [domain, setDomain] = useState<string>('');
  const [hasExistingAnalysis, setHasExistingAnalysis] = useState<boolean | null>(null); // null = non vérifié, true/false = résultat de la vérification
  const [apiTestResult, setApiTestResult] = useState<any>(null);
  const [isTestingApi, setIsTestingApi] = useState<boolean>(false);
  const [showApiTest, setShowApiTest] = useState<boolean>(false);

  useEffect(() => {
    const processAndSetDomain = (urlToProcess: string) => {
      if (!urlToProcess) return;
      let currentUrl = urlToProcess.trim();
      if (!currentUrl.startsWith('http://') && !currentUrl.startsWith('https://')) {
        currentUrl = 'https://' + currentUrl;
      }
      try {
        const urlObj = new URL(currentUrl);
        const hostname = urlObj.hostname;
        const extractedDomain = hostname.startsWith('www.') ? hostname.substring(4) : hostname;
        setDomain(extractedDomain);
      } catch (e) {
        console.error("Erreur lors de l'extraction du domaine:", e);
        setDomain(urlToProcess); // Fallback
      }
    };

    const fetchProspectUrl = async () => {
      if (prospectId) {
        try {
          const { data, error: dbError } = await supabase
            .from('prospects')
            .select('site_url')
            .eq('id', prospectId)
            .maybeSingle();

          if (dbError) {
          } else if (data && data.site_url) {
            processAndSetDomain(data.site_url);
          }
        } catch (err) {
        }
      } else if (siteUrl) {
        processAndSetDomain(siteUrl);
      }
    };

    fetchProspectUrl();
  }, [siteUrl, prospectId]);

  useEffect(() => {
    if (domain && prospectId) {
      const checkAnalysisHistory = async () => {
        try {
          const { data, error: dbError } = await supabase
            .from('keyword_analysis_results')
            .select('id')
            .eq('prospect_id', prospectId)
            .eq('domain', domain)
            .eq('analysis_type', AnalysisType.RANKED_KEYWORDS)
            .maybeSingle();

          if (dbError && dbError.code !== 'PGRST116') {
            setHasExistingAnalysis(false);
          } else if (data) {
            setHasExistingAnalysis(true);
            checkExistingData();
          } else {
            setHasExistingAnalysis(false);
          }
        } catch (err) {
          setHasExistingAnalysis(false);
        }
      };

      checkAnalysisHistory();
    }
  }, [domain, prospectId]);

  const loadRankedKeywordsData = async (isRefresh: boolean = false) => {
    if (!domain) {
      setError('Aucun domaine spécifié pour l\'analyse.');
      return;
    }

    if (!isRefresh) setLoading(true);
    setIsRefreshing(isRefresh);
    setError(null);

    try {
      const keywordsData = await callEdge('dataforseo-keywords', {
        domain,
        prospectId,
        forceRefresh: isRefresh
      });

      if (keywordsData && keywordsData.length > 0) {
        const rankedKeywordsData: RankedKeywordData[] = keywordsData.map((kw: any) => ({
          keyword: kw.keyword,
          rank_group: kw.rank_group || kw.rank_absolute || 0,
          rank_absolute: kw.rank_absolute || 0,
          url: kw.url || `https://${domain}`,
          search_volume: kw.search_volume || 0,
          cpc: kw.cpc || 0,
          competition: kw.competition || 0,
          etv: kw.etv || 0,
          estimated_paid_traffic_cost: kw.estimated_paid_traffic_cost || 0,
          title: kw.title || '',
          description: kw.description || ''
        }));

        setRankedKeywords(rankedKeywordsData);
      } else {
        setRankedKeywords([]);
        setError('Aucun mot-clé classé trouvé pour ce domaine.');
      }
    } catch (err) {
      setError('Erreur lors du chargement des mots-clés classés.');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  // Cette fonction vérifie s'il existe des données sauvegardées, mais ne s'exécute pas automatiquement
  const checkExistingData = async () => {
    if (domain && prospectId) {
      setLoading(true);
      try {
        const { data, error: dbError } = await supabase
          .from('keyword_analysis_results')
          .select('ranked_keywords')
          .eq('prospect_id', prospectId)
          .eq('domain', domain)
          .eq('analysis_type', AnalysisType.RANKED_KEYWORDS)
          .maybeSingle();

        if (dbError && dbError.code !== 'PGRST116') {
          setError('Erreur lors de la récupération des résultats.');
        } else if (data && data.ranked_keywords && data.ranked_keywords.length > 0) {
          setRankedKeywords(data.ranked_keywords);
        } else {
          setRankedKeywords([]);
        }
      } catch (err) {
        setError('Erreur lors de la récupération des résultats.');
      } finally {
        setLoading(false);
      }
    }
  };
  
  // Pas d'appel automatique au chargement du composant

  const handleRefresh = () => {
    loadRankedKeywordsData(true);
  };

  const handleCheckExisting = () => {
    checkExistingData();
  };

  const handleTestApi = async () => {
    if (!domain) {
      setError('Aucun domaine spécifié pour le test API.');
      return;
    }

    setIsTestingApi(true);
    setApiTestResult(null);

    try {
      const result = await callEdge('dataforseo-keywords', {
        action: 'test',
        domain
      });
      setApiTestResult(result);
    } catch (err) {
      setApiTestResult({
        success: false,
        error: 'Exception',
        details: err instanceof Error ? err.message : String(err)
      });
    } finally {
      setIsTestingApi(false);
    }
  };

  const toggleApiTest = () => {
    setShowApiTest(!showApiTest);
    if (!showApiTest) {
      setApiTestResult(null);
    }
  };

  const renderContent = () => {
    if (loading && !isRefreshing) {
      return (
        <div className="p-4 text-center flex flex-col items-center">
          <Loader className="animate-spin w-6 h-6 text-blue-500 mb-2" />
          <p className="text-sm text-gray-500">Analyse des mots-clés en cours...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="p-4 text-center flex flex-col items-center">
          <AlertTriangle className="w-6 h-6 text-orange-500 mb-2" />
          <p className="text-sm text-gray-500">{error}</p>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="mt-3 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {isRefreshing ? 'Analyse en cours...' : 'Lancer l\'analyse'}
          </button>
        </div>
      );
    }

    if (!rankedKeywords || rankedKeywords.length === 0) {
      return (
        <div className="p-4 text-center flex flex-col items-center">
          <Search className="w-6 h-6 text-gray-400 mb-2" />
          <p className="text-sm text-gray-500 mb-3">Aucune analyse SEO n'a encore été effectuée pour ce domaine.</p>
          <div className="flex space-x-2">
            {/* Afficher le bouton "Vérifier données existantes" uniquement lors de la première visite ou si l'état n'a pas encore été déterminé */}
            {(hasExistingAnalysis === false || hasExistingAnalysis === null) && (
              <button
                onClick={handleCheckExisting}
                disabled={loading}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
              >
                {loading ? 'Chargement...' : 'Vérifier données existantes'}
              </button>
            )}
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {isRefreshing ? 'Analyse en cours...' : 'Lancer l\'analyse'}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
        <div className="grid grid-cols-12 text-xs font-medium text-gray-500 border-b pb-1 mb-2 sticky top-0 bg-white z-10">
          <div className="col-span-6 px-2 py-1">Mot-clé</div>
          <div className="col-span-3 text-center px-2 py-1">Rang</div>
          <div className="col-span-3 text-right px-2 py-1">Volume</div>
        </div>
        {rankedKeywords.slice(0, 10).map((keyword: RankedKeywordData, index: number) => (
          <div key={index} className="grid grid-cols-12 py-1 text-sm border-b border-gray-100 hover:bg-gray-50">
            <div className="col-span-6 truncate px-2" title={keyword.keyword}>
              {keyword.keyword}
            </div>
            <div className="col-span-3 text-center px-2">
              <span
                className={`px-1.5 py-0.5 rounded-md text-xs font-medium ${
                  keyword.rank_absolute === 0 ? 'bg-gray-100 text-gray-600' :
                  keyword.rank_absolute <= 3 ? 'bg-green-100 text-green-800' :
                  keyword.rank_absolute <= 10 ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-100 text-gray-800'
                }`}
              >
                {keyword.rank_absolute === 0 ? 'Non classé' : `#${keyword.rank_absolute}`}
              </span>
            </div>
            <div className="col-span-3 text-right px-2">
              {keyword.search_volume ? keyword.search_volume.toLocaleString() : '-'}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200 flex flex-col h-full">
      <div className="flex justify-between items-center px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
        <div className="flex items-center">
          <TrendingUp className="h-4 w-4 text-blue-500 mr-2" />
          <h3 className="text-sm font-medium text-gray-700">Mots-clés Classés</h3>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={toggleApiTest}
            className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
          >
            <Wrench className="h-3 w-3 mr-1" />
            Diagnostiquer API
          </button>
          <button
            onClick={handleRefresh}
            disabled={loading || isRefreshing}
            className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50"
          >
            {isRefreshing ? (
              <Loader className="animate-spin h-3 w-3 mr-1" />
            ) : (
              <RefreshCw className="h-3 w-3 mr-1" />
            )}
            Rafraîchir
          </button>
        </div>
      </div>
      
      <div className="p-1 flex-grow overflow-y-auto">
        {showApiTest ? (
          <div className="p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Diagnostic de l'API DataForSEO</h4>
            <p className="text-xs text-gray-500 mb-3">
              Cet outil permet de tester la connexion à l'API DataForSEO et de diagnostiquer les problèmes potentiels.
            </p>
            
            <button
              onClick={handleTestApi}
              disabled={isTestingApi}
              className="mb-4 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {isTestingApi ? (
                <>
                  <Loader className="animate-spin h-3 w-3 mr-2" />
                  Test en cours...
                </>
              ) : (
                <>Tester l'API pour {domain || 'ce domaine'}</>
              )}
            </button>
            
            {apiTestResult && (
              <div className="mt-3 border rounded-md p-3 bg-gray-50 text-xs">
                <div className="font-medium mb-2">
                  Statut: 
                  <span className={apiTestResult.success ? 'text-green-600' : 'text-red-600'}>
                    {apiTestResult.success ? 'Succès' : 'Échec'}
                  </span>
                </div>
                
                {apiTestResult.response && (
                  <div className="mb-2">
                    <div className="font-medium">Réponse HTTP:</div>
                    <div className="pl-2 mt-1">
                      <div>Status: {apiTestResult.response.status} {apiTestResult.response.statusText}</div>
                    </div>
                  </div>
                )}
                
                {apiTestResult.error && (
                  <div className="mb-2">
                    <div className="font-medium text-red-600">Erreur:</div>
                    <div className="pl-2 mt-1 text-red-600">{apiTestResult.error}</div>
                    {apiTestResult.details && (
                      <div className="pl-2 mt-1 whitespace-pre-wrap text-red-500">
                        {typeof apiTestResult.details === 'string' ? apiTestResult.details : JSON.stringify(apiTestResult.details, null, 2)}
                      </div>
                    )}
                  </div>
                )}
                
                {apiTestResult.data && (
                  <div>
                    <div className="font-medium">Données de réponse:</div>
                    <div className="pl-2 mt-1">
                      <div>Code de statut API: {apiTestResult.data.status_code}</div>
                      <div>Message: {apiTestResult.data.status_message}</div>
                      {apiTestResult.data.tasks && (
                        <div>Nombre de tâches: {apiTestResult.data.tasks.length}</div>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="mt-3 text-gray-500">
                  Consultez la console du navigateur pour plus de détails.
                </div>
              </div>
            )}
          </div>
        ) : (
          renderContent()
        )}
      </div>
      
      {rankedKeywords && rankedKeywords.length > 10 && (
        <div className="px-4 py-2 bg-gray-50 text-xs text-gray-500 text-right border-t border-gray-200">
          Affichage de 10 mots-clés sur {rankedKeywords.length}
        </div>
      )}
    </div>
  );
};

export default KeywordAnalysisCard;
